import React, { useEffect, useState, useCallback } from 'react';
import { Card, Table, Button, Input, Select, Tag, Space, Modal, notification, Row, Col, Statistic } from 'antd';
import { SearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, SolutionOutlined } from '@ant-design/icons';
import axios from 'axios';
import { baseUrl } from '../../config';

const { Option } = Select;
const { TextArea } = Input;

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('accessToken')}` });

const STATUS_TAG = {
  Pending:  <Tag color="orange">⏳ Pending</Tag>,
  Approved: <Tag color="green">✅ Approved</Tag>,
  Rejected: <Tag color="red">❌ Rejected</Tag>,
  Expired:  <Tag color="default">⌛ Expired</Tag>,
};

const JOB_TYPE_COLOR = { 'Full Time': '#7a0000', 'Part Time': '#7c3aed', 'Contract': '#0891b2', 'Freelance': '#c2410c', 'Other': '#6b7280' };

export default function JobsList() {
  const [data,     setData]     = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(1);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const [status,   setStatus]   = useState('');
  const [stats,    setStats]    = useState({});
  const [viewItem, setViewItem] = useState(null);
  const [reviewItem, setReviewItem] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [reviewing,  setReviewing]  = useState(false);

  const fetchStats = useCallback(async () => {
    try { const r = await axios.get(`${baseUrl}/admin/jobs/stats`, { headers: auth() }); if (r.data.status) setStats(r.data.result || {}); } catch {}
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30, ...(search ? { search } : {}), ...(status ? { status } : {}) });
      const r = await axios.get(`${baseUrl}/admin/jobs?${params}`, { headers: auth() });
      if (r.data.status) { setData(r.data.result || []); setTotal(r.data.total || 0); }
    } catch { notification.error({ message: 'Failed to load', placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [page, search, status]);

  useEffect(() => { fetchData(); fetchStats(); }, [fetchData, fetchStats]);

  const handleReview = async (action) => {
    if (action === 'reject' && !rejectNote.trim()) { notification.warning({ message: 'Rejection reason required', placement: 'topRight' }); return; }
    setReviewing(true);
    try {
      const r = await axios.patch(`${baseUrl}/admin/jobs/${reviewItem.id}/review`, { action, admin_notes: rejectNote }, { headers: auth() });
      if (r.data.status) {
        notification.success({ message: action === 'approve' ? 'Job Approved ✅' : 'Job Rejected ❌', placement: 'topRight' });
        setReviewItem(null); setRejectNote(''); fetchData(); fetchStats();
      }
    } catch (err) { notification.error({ message: err?.response?.data?.message || 'Error', placement: 'topRight' }); }
    finally { setReviewing(false); }
  };

  const columns = [
    { title: '#', dataIndex: 'id', width: 55 },
    { title: 'Job', render: (_, r) => (
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{r.title}</div>
        {r.company_name && <div style={{ fontSize: 11, color: '#6b7280' }}>{r.company_name}</div>}
        <Tag style={{ marginTop: 3 }} color={JOB_TYPE_COLOR[r.job_type] || '#7a0000'}>{r.job_type}</Tag>
      </div>
    )},
    { title: 'Posted By', render: (_, r) => (<div><div style={{ fontSize: 13 }}>{r.posted_by_name}</div><div style={{ fontSize: 11, color: '#6b7280' }}>{r.posted_by_phone}</div></div>) },
    { title: 'Location', render: (_, r) => <span style={{ fontSize: 12 }}>{[r.location_city, r.location_state].filter(Boolean).join(', ') || '—'}</span> },
    { title: 'Status', dataIndex: 'status', render: v => STATUS_TAG[v] },
    { title: 'Actions', render: (_, r) => (
      <Space>
        <Button size="small" icon={<EyeOutlined />} onClick={() => setViewItem(r)} />
        {r.status === 'Pending' && <Button size="small" type="primary" onClick={() => { setReviewItem(r); setRejectNote(''); }} style={{ background: '#7a0000' }}>Review</Button>}
      </Space>
    )},
  ];

  return (
    <div style={{ padding: '12px 0' }}>
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {[
          { label: 'Total', value: stats.total || 0, color: '#7a0000' },
          { label: 'Pending', value: stats.pending || 0, color: '#d97706' },
          { label: 'Approved', value: stats.approved || 0, color: '#16a34a' },
          { label: 'Rejected', value: stats.rejected || 0, color: '#dc2626' },
        ].map(s => (
          <Col key={s.label} xs={12} sm={6}>
            <Card size="small" style={{ borderLeft: `4px solid ${s.color}`, borderRadius: 10 }} bodyStyle={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, color: '#6b7280' }}>{s.label}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title={<><SolutionOutlined style={{ color: '#7a0000', marginRight: 8 }} />Job Board Management</>}
        extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchData(); fetchStats(); }}>Refresh</Button>}
      >
        <Row gutter={[10, 10]} style={{ marginBottom: 14 }}>
          <Col xs={24} sm={12} md={10}>
            <Input prefix={<SearchOutlined />} placeholder="Search job, person, company..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} allowClear />
          </Col>
          <Col xs={12} sm={6} md={4}>
            <Select style={{ width: '100%' }} placeholder="Status" value={status || undefined} onChange={v => { setStatus(v || ''); setPage(1); }} allowClear>
              <Option value="Pending">Pending</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="Expired">Expired</Option>
            </Select>
          </Col>
        </Row>

        <Table dataSource={data} columns={columns} rowKey="id" loading={loading} size="small" scroll={{ x: 700 }}
          pagination={{ current: page, pageSize: 30, total, onChange: setPage, showTotal: t => `Total ${t}` }} />
      </Card>

      {/* View Modal */}
      <Modal open={!!viewItem} title={viewItem?.title} onCancel={() => setViewItem(null)}
        footer={[
          viewItem?.status === 'Pending' && <Button key="r" type="primary" onClick={() => { setReviewItem(viewItem); setViewItem(null); setRejectNote(''); }} style={{ background: '#7a0000' }}>Review</Button>,
          <Button key="c" onClick={() => setViewItem(null)}>Close</Button>,
        ].filter(Boolean)} width={520}>
        {viewItem && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['Company', viewItem.company_name], ['Job Type', viewItem.job_type],
              ['Location', [viewItem.location_city, viewItem.location_state].filter(Boolean).join(', ')],
              ['Salary', viewItem.salary_range], ['Contact', viewItem.contact_phone || viewItem.contact_email],
              ['Last Date', viewItem.last_date?.slice(0, 10)], ['Posted By', `${viewItem.posted_by_name} (${viewItem.posted_by_phone})`],
            ].filter(([, v]) => v).map(([l, v]) => (
              <div key={l}><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>{l}</div><div style={{ fontSize: 13 }}>{v}</div></div>
            ))}
            {viewItem.description && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Description</div><div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{viewItem.description}</div></div>}
            {viewItem.requirements && <div><div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase' }}>Requirements</div><div style={{ fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{viewItem.requirements}</div></div>}
          </div>
        )}
      </Modal>

      {/* Review Modal */}
      <Modal open={!!reviewItem} title={`Review: ${reviewItem?.title}`} onCancel={() => { setReviewItem(null); setRejectNote(''); }} footer={null} width={460}>
        {reviewItem && (
          <div>
            <div style={{ background: '#f9fafb', borderRadius: 8, padding: '10px 14px', marginBottom: 14 }}>
              <div style={{ fontWeight: 700 }}>{reviewItem.company_name || '—'}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{reviewItem.job_type} · {reviewItem.location_city}</div>
            </div>
            <TextArea rows={3} value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Rejection reason (required for reject)..." style={{ marginBottom: 14 }} />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button onClick={() => { setReviewItem(null); setRejectNote(''); }}>Cancel</Button>
              <Button danger loading={reviewing} icon={<CloseCircleOutlined />} onClick={() => handleReview('reject')}>Reject</Button>
              <Button type="primary" loading={reviewing} icon={<CheckCircleOutlined />} onClick={() => handleReview('approve')} style={{ background: '#16a34a', borderColor: '#16a34a' }}>Approve</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
